import { useCallback, useEffect, useRef, useState } from 'react';

const emptyPreviewState = {
    isOpen: false,
    isLoading: false,
    error: '',
    file: null,
};

export const useFilePreview = (downloadFile) => {
    const [previewState, setPreviewState] = useState(emptyPreviewState);
    const objectUrlRef = useRef(null);
    const requestIdRef = useRef(0);

    const revokeObjectUrl = useCallback(() => {
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
    }, []);

    const closePreview = useCallback(() => {
        requestIdRef.current += 1;
        revokeObjectUrl();
        setPreviewState(emptyPreviewState);
    }, [revokeObjectUrl]);

    const openPreview = useCallback(async (fileId, fileName, fileType = '') => {
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;
        revokeObjectUrl();
        setPreviewState({
            isOpen: true,
            isLoading: true,
            error: '',
            file: {
                id: fileId,
                fileName,
                fileType,
                url: '',
                blob: null,
            },
        });

        try {
            const blob = await downloadFile(fileId);
            if (requestIdRef.current !== requestId) {
                return;
            }

            const url = URL.createObjectURL(blob);
            if (requestIdRef.current !== requestId) {
                URL.revokeObjectURL(url);
                return;
            }

            objectUrlRef.current = url;

            setPreviewState({
                isOpen: true,
                isLoading: false,
                error: '',
                file: {
                    id: fileId,
                    fileName,
                    fileType: fileType || blob.type || '',
                    url,
                    blob,
                },
            });
        } catch (error) {
            if (requestIdRef.current !== requestId) {
                return;
            }

            setPreviewState((prev) => ({
                ...prev,
                isLoading: false,
                error: error.message || 'Preview failed',
            }));
        }
    }, [downloadFile, revokeObjectUrl]);

    useEffect(() => revokeObjectUrl, [revokeObjectUrl]);

    return {
        previewState,
        openPreview,
        closePreview,
    };
};
